import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  apiCreateOrder,
  apiForgotPassword,
  apiGetOrder,
  apiGetOrders,
  apiGetPrice,
  apiGetPrices,
  apiGetRoster,
  apiResetPassword,
  apiUpdateEmployee,
} from "@/lib/api";
import type {
  CreateOrderInput,
  CreateOrderResponse,
  Employee,
  Order,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function usePrice(): UseQueryResult<{ unitPrice: number }> {
  return useQuery({
    queryKey: ["price"],
    queryFn: apiGetPrice,
    staleTime: 10 * 60 * 1000,
  });
}

/** Both product prices. The key sits under the "price" prefix on purpose: the
 *  session-end handler (auth.tsx) keeps every "price" query, because the public
 *  checkout shows prices to a logged-out vendor. */
export function usePrices(): UseQueryResult<{ bcbPrice: number; badgePrice: number }> {
  return useQuery({
    queryKey: ["price", "all"],
    queryFn: apiGetPrices,
    staleTime: 10 * 60 * 1000,
  });
}

/** The vendor's roster. Per-vendor data: the session-end handler drops it. */
export function useRoster(): UseQueryResult<{ employees: Employee[] }> {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["roster"],
    queryFn: apiGetRoster,
    enabled: isAuthenticated,
  });
}

export function useUpdateEmployee(): UseMutationResult<
  { employee: Employee },
  Error,
  { id: string; inactive: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, inactive }: { id: string; inactive: boolean }) =>
      apiUpdateEmployee(id, inactive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["roster"] });
    },
  });
}

export function useOrders(): UseQueryResult<{
  orders: Order[];
  meta: Record<string, unknown>;
}> {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["orders"],
    queryFn: apiGetOrders,
    enabled: isAuthenticated,
  });
}

export function useOrderDetail(
  id: string,
): UseQueryResult<{ order: Order; employees: Employee[] }> {
  return useQuery({
    queryKey: ["orders", id],
    queryFn: () => apiGetOrder(id),
    enabled: !!id,
  });
}

export function useCreateOrder(): UseMutationResult<
  CreateOrderResponse,
  Error,
  CreateOrderInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderInput) => apiCreateOrder(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      // An order creates or moves employees; a stale roster invites a re-order.
      void queryClient.invalidateQueries({ queryKey: ["roster"] });
    },
  });
}

export function useLogin(): UseMutationResult<
  void,
  Error,
  { email: string; password: string }
> {
  const { login } = useAuth();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      login(email, password),
  });
}

export function useForgotPassword(): UseMutationResult<
  { message: string },
  Error,
  string
> {
  return useMutation({
    mutationFn: (email: string) => apiForgotPassword(email),
  });
}

export function useResetPassword(): UseMutationResult<
  { message: string },
  Error,
  { token: string; password: string }
> {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      apiResetPassword(token, password),
  });
}
